import { UserDataType } from "types/autentication"
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { notificationStatus } from 'constants/notificationStatus'
import NotificationService, { normalizeIshikawaApiResponseBody } from 'services/Notification'
import {
    getGroupNotificationSysId,
    partitionNotificationsByProtocolMap
} from '../GroupLondonProtocol/helpers'

export interface GroupNotificationItem {
  u_reclassified_incident_type?: any
  number: { value: string; display_value: string }
  incident_title: { value: string; display_value: string }
  incident_description: { value: string; display_value: string }
  sys_created_on: { value: string; display_value: string }
  sys_id?: { value: string; display_value: string }
  u_image_risk?: { value: string; display_value: string }
  damage_level?: { value: string; display_value: string }
  u_probability_occurence?: { value: string; display_value: string }
  u_never_events_or_sentinel_event?: { value: string; display_value: string }
  location_incident?: { value: string; display_value: string }
  incident_sector?: { value: string; display_value: string }
  u_assigned_to?: { value: string; display_value: string }
  selected?: boolean
}

export interface GroupDataInfo {
  u_notifica_user: { value: string; display_value: string }
  u_number: { value: string; display_value: string }
  u_related_notifications: { value: string; display_value: string }
  u_sector: { value: string; display_value: string }
  u_incident_type: { value: string; display_value: string }
  u_group_damage_rating: { value: string; display_value: string }
  u_location: { value: string; display_value: string }
  sys_id: { value: string; display_value: string }
}

export interface GroupData {
  group_key: string
  group_info: GroupDataInfo
  total_notifications: number
  notifications: GroupNotificationItem[]
}

/**
 * Determina se o usuário pode editar o diagrama de Ishikawa baseado na primeira notificação
 * corporate_quality_board NÃO pode editar notificações em grupo
 */
export const canEditIshikawaPerfil = (
    { loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState }:
        { loggedInUserIsResponsible: boolean, isSameLocation: boolean, isSameSector: boolean, notificationStatusState: string }) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [
            listOfRoles.admin,
            listOfRoles.location_qa
        ]
    } else {
        return [
            ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
            ...(isSameLocation && isSameSector && loggedInUserIsResponsible ? [listOfRoles.location_manager] : []),
            ...(isSameLocation && loggedInUserIsResponsible ? [listOfRoles.location_qa] : []),
        ]
    }
}

/**
 * Calcula se o diagrama de Ishikawa é obrigatório para um grupo baseado na PRIMEIRA notificação
 * @param firstNotification - Primeira notificação do grupo (referência)
 * @returns boolean - true se o diagrama deve ser obrigatório
 */
export function calculateGroupIshikawaMandatory(firstNotification?: GroupNotificationItem): boolean {
  if (!firstNotification) {
    return false;
  }

  // Verificar se tem risco de imagem
  const hasImageRisk = firstNotification.u_image_risk?.value === 'u_image_risk_yes' ||
                      firstNotification.u_image_risk?.value === 'true';

  // Verificar se tem Never Events
  const hasNeverEvents = firstNotification.u_never_events_or_sentinel_event?.value === 'yes';

  // Se tem risco de imagem ou Never Events, é obrigatório
  if (hasImageRisk || hasNeverEvents) {
    return true;
  }

  // Verificar grau de dano crítico (Óbito ou Grave)
  const damageLevel = firstNotification.damage_level?.value;
  const criticalDamages = ['death', 'severe']; // valores que tornam obrigatório

  if (criticalDamages.includes(damageLevel || '')) {
    return true;
  }

  // Para outros casos, verificar a probabilidade de ocorrência
  const probability = parseInt(firstNotification.u_probability_occurence?.value || '0');

  // Se probabilidade alta (4 ou 5), pode ser obrigatório dependendo do dano
  if (probability >= 4) {
    return true;
  }

  return false;
}

/** sys_id string ou referência ServiceNow { value } — igual ao protocolo de Londres */
export const getIshikawaRecordId = (ishikawa?: Record<string, any> | null): string | undefined => {
    if (!ishikawa) return undefined
    const s = ishikawa.sys_id
    if (s == null || s === '') return undefined
    if (typeof s === 'string' && s.trim() !== '') return s.trim()
    if (typeof s === 'object' && s !== null && 'value' in s) {
        const v = (s as { value?: string }).value
        if (v && v.trim() !== '') return v
    }
    return undefined
}

export const buildIshikawaSysIdByNotificationMap = async (
    groupData: GroupData
): Promise<Map<string, string>> => {
    const map = new Map<string, string>()
    const notifications = groupData?.notifications || []
    await Promise.all(
        notifications.map(async (notification) => {
            const notifId = getGroupNotificationSysId(notification)
            if (!notifId) return
            const row = await NotificationService.getIshikawaRowByNotificationId(notifId)
            const id = getIshikawaRecordId(row)
            if (id) map.set(notifId, id)
        })
    )
    return map
}

export const mergeFirstIshikawaIdFromRedux = (
    groupData: GroupData,
    map: Map<string, string>,
    ishikawaFromRedux?: Record<string, any> | null
): Map<string, string> => {
    const firstN = groupData?.notifications?.[0]
    const notifId = getGroupNotificationSysId(firstN)
    const row =
        normalizeIshikawaApiResponseBody(ishikawaFromRedux) ??
        (ishikawaFromRedux && typeof ishikawaFromRedux === 'object' && !Array.isArray(ishikawaFromRedux)
            ? (ishikawaFromRedux as Record<string, any>)
            : null)
    const fromRedux = getIshikawaRecordId(row)
    if (notifId && fromRedux && !map.has(notifId)) {
        map.set(notifId, fromRedux)
    }
    return map
}

function normalizeUNotificaUserField(
    fromPayload: unknown,
    currentUser: UserDataType,
    groupData: GroupData
): string {
    const fallback = currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || ''
    if (fromPayload == null || fromPayload === '') return fallback
    if (typeof fromPayload === 'string') return fromPayload
    if (typeof fromPayload === 'object' && fromPayload !== null && 'value' in fromPayload) {
        const v = (fromPayload as { value?: string }).value
        return v || fallback
    }
    return fallback
}

/**
 * Batch CREATE de "não realizar" só para notificações sem diagrama existente
 */
export function convertNotPerformIshikawaCreateBatchForNotifications(
    notifications: GroupNotificationItem[],
    groupData: GroupData,
    currentUser: UserDataType,
    doNotPerform: boolean = true
): any {
    const records = notifications.map((notification) => ({
        hospital_incident: getGroupNotificationSysId(notification),
        do_not_perform_optional: doNotPerform,
        method: '',
        measurement: '',
        material: '',
        environment: '',
        machine: '',
        manpower: '',
        description:
            notification.u_reclassified_incident_type?.display_value?.trim() ||
            notification.incident_title?.display_value?.trim() ||
            ' ',
        u_notifica_user: normalizeUNotificaUserField(undefined, currentUser, groupData)
    }))

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}

/**
 * Converte dados do formulário "Não realizar" para formato batch CREATE (grupo inteiro)
 */
export function convertNotPerformIshikawaToBatchRequest(
    groupData: GroupData,
    currentUser: UserDataType,
    doNotPerform: boolean = true
): any {
    if (!groupData?.notifications) {
        throw new Error('Dados do grupo não encontrados')
    }
    return convertNotPerformIshikawaCreateBatchForNotifications(
        groupData.notifications,
        groupData,
        currentUser,
        doNotPerform
    )
}

/**
 * Batch UPDATE de "não realizar": um sys_id por notificação; payload alinhado ao fluxo individual
 * (IshikawaDiagram/index.tsx — sem categories/problem no body).
 */
export function convertNotPerformIshikawaUpdateBatchRequest(
    notifications: GroupNotificationItem[],
    ishikawaTemplate: Record<string, any>,
    doNotPerform: boolean,
    ishikawaSysIdByNotificationId: Map<string, string>,
    currentUser: UserDataType,
    groupData: GroupData
): any {
    const tpl =
        normalizeIshikawaApiResponseBody(ishikawaTemplate) ??
        (ishikawaTemplate || {})
    const uUser = normalizeUNotificaUserField(tpl.u_notifica_user, currentUser, groupData)

    const records = notifications.map((notification) => {
        const notifId = getGroupNotificationSysId(notification)
        const diagramId = notifId ? ishikawaSysIdByNotificationId.get(notifId) : undefined
        if (!notifId || !diagramId) {
            throw new Error(
                'Lista de notificacoes com update de Ishikawa deve conter so itens com sys_id de diagrama'
            )
        }
        const clearFields = doNotPerform
        return {
            sys_id: diagramId,
            hospital_incident: notifId,
            do_not_perform_optional: doNotPerform,
            method: clearFields ? '' : (tpl.method ?? ''),
            measurement: clearFields ? '' : (tpl.measurement ?? ''),
            material: clearFields ? '' : (tpl.material ?? ''),
            environment: clearFields ? '' : (tpl.environment ?? ''),
            machine: clearFields ? '' : (tpl.machine ?? ''),
            manpower: clearFields ? '' : (tpl.manpower ?? ''),
            description:
                tpl.description ??
                notification.u_reclassified_incident_type?.display_value ??
                ' ',
            u_notifica_user: uUser
        }
    })

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}

/**
 * Batch CREATE do formulário completo para subconjunto de notificações
 */
export function convertIshikawaToBatchRequestForNotifications(
    notifications: GroupNotificationItem[],
    ishikawaData: any,
    groupData: GroupData,
    currentUser: UserDataType
): any {
    const records = notifications
        .filter((n) => n.selected !== false)
        .map((notification) => ({
            hospital_incident: getGroupNotificationSysId(notification),
            method: ishikawaData.method || '',
            measurement: ishikawaData.measurement || '',
            material: ishikawaData.material || '',
            environment: ishikawaData.environment || '',
            machine: ishikawaData.machine || '',
            manpower: ishikawaData.manpower || '',
            description: ishikawaData.description || '',
            u_notifica_user: normalizeUNotificaUserField(undefined, currentUser, groupData),
            do_not_perform_optional: 'false'
        }))

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}

/**
 * Converte dados do diagrama de Ishikawa para formato batch CREATE (grupo inteiro)
 */
export function convertIshikawaToBatchRequest(
    ishikawaData: any,
    groupData: GroupData,
    currentUser: UserDataType
): any {
    if (!groupData?.notifications) {
        throw new Error('Dados do grupo não encontrados')
    }
    return convertIshikawaToBatchRequestForNotifications(
        groupData.notifications,
        ishikawaData,
        groupData,
        currentUser
    )
}

/**
 * Batch UPDATE do formulário completo: cada record com sys_id do diagrama da notificação
 */
export function convertIshikawaFullFormUpdateBatchRequest(
    notifications: GroupNotificationItem[],
    ishikawaData: any,
    ishikawaSysIdByNotificationId: Map<string, string>,
    currentUser: UserDataType,
    groupData: GroupData
): any {
    const uUser = normalizeUNotificaUserField(undefined, currentUser, groupData)
    const records = notifications
        .filter((n) => n.selected !== false)
        .map((notification) => {
            const notifId = getGroupNotificationSysId(notification)
            const diagramId = notifId ? ishikawaSysIdByNotificationId.get(notifId) : undefined
            if (!notifId || !diagramId) {
                throw new Error(
                    'Lista de notificacoes com update de Ishikawa deve conter so itens com sys_id de diagrama'
                )
            }
            return {
                sys_id: diagramId,
                hospital_incident: notifId,
                method: ishikawaData.method || '',
                measurement: ishikawaData.measurement || '',
                material: ishikawaData.material || '',
                environment: ishikawaData.environment || '',
                machine: ishikawaData.machine || '',
                manpower: ishikawaData.manpower || '',
                description: ishikawaData.description || '',
                u_notifica_user: uUser,
                do_not_perform_optional: 'false'
            }
        })

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}

export { partitionNotificationsByProtocolMap }
